import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Form, Spinner, Row, Col } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { generatePath } from 'react-router';
import { useTranslation } from 'react-i18next';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import { ImageInput, SelectBox } from '#atoms';
import { LaboratoryForm, DocumentInfoForm, DiagnosisForm } from '#components';
import {
  useCreateMedicalDocumentMutation,
  useProcessMedicalDocumentMutation,
  useUpdateMedicalDocumentMutation,
  useFetchSubscriptionStatusQuery,
} from '#store';
import { TagsInput } from '#atoms';
import { alert } from '#utils';
import { Routes } from '#Routes';

const stickyColumnStyle = {
  position: 'sticky',
  top: '20px', // Adjust this value based on your layout
  height: 'calc(100vh - 200px)', // Adjust based on your layout
  overflowY: 'auto',
};

const scrollableColumnStyle = {
  maxHeight: 'calc(100vh - 200px)', // Match the height of sticky column
  overflowY: 'auto',
};

function isLabOrDiagType(v) {
  return v === 'LAB' || v === 'DIAG';
}

export const MedicalDocumentForm = ({ type, data }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const warningShownRef = useRef(false);
  const prevDocumentTypeRef = useRef();
  /** Ostatni wybrany typ (LAB/DIAG) — watch() potrafi na jedną klatkę zwrócić pustę po re-renderze (np. subskrypcja). */
  const [lastKnownDocumentType, setLastKnownDocumentType] = useState(null);

  const createFormOptions = useMemo(
    () => ({
      defaultValues: {
        document_type: undefined,
        document_images: [],
        process_type: undefined,
      },
      shouldUnregister: false,
    }),
    [],
  );

  const form = useForm(
    data != null ? { values: data, shouldUnregister: false } : createFormOptions,
  );

  const { handleSubmit, watch, setValue, formState: { dirtyFields } } = form;

  const [updateMedicalDocumentMutation, { isLoading: isLoadingUpdate }] =
    useUpdateMedicalDocumentMutation();
  const [createMedicalDocumentMutation, { isLoading: isLoadingCreate }] =
    useCreateMedicalDocumentMutation();
  const [processMedicalDocumentMutation] = useProcessMedicalDocumentMutation();

  const { data: subscriptionStatus } = useFetchSubscriptionStatusQuery();

  const DocumentType = watch('document_type');
  const DocumentImages = watch('document_images');
  const ProcessType = watch('process_type');

  const displayDocumentType =
    type === 'CREATE' && isLabOrDiagType(DocumentType)
      ? DocumentType
      : type === 'CREATE' && isLabOrDiagType(lastKnownDocumentType)
        ? lastKnownDocumentType
        : DocumentType;

  useEffect(() => {
    if (isLabOrDiagType(DocumentType)) {
      setLastKnownDocumentType(DocumentType);
    }
  }, [DocumentType]);

  useEffect(() => {
    if (type !== 'CREATE' || !lastKnownDocumentType) return;
    if (!isLabOrDiagType(DocumentType)) {
      setValue('document_type', lastKnownDocumentType, { shouldValidate: false });
    }
  }, [DocumentType, lastKnownDocumentType, setValue, type]);

  useEffect(() => {
    if (!isLabOrDiagType(DocumentType)) return;
    const prev = prevDocumentTypeRef.current;
    if (isLabOrDiagType(prev) && prev !== DocumentType) {
      setValue('document_images', []);
      setValue('process_type', undefined);
      warningShownRef.current = false;
    }
    prevDocumentTypeRef.current = DocumentType;
  }, [DocumentType, setValue]);

  useEffect(() => {
    if (DocumentImages?.length > 0 && !warningShownRef.current) {
      warningShownRef.current = true;
      alert.fire({
        icon: 'warning',
        title: t('document-form.personal-data-warning-title'),
        text: t('document-form.personal-data-warning-text'),
        confirmButtonText: 'OK',
        allowOutsideClick: false,
      });
    }
  }, [DocumentImages, t]);

  useEffect(() => {
    if (type !== 'EDIT') {
      // Use service_availability for MEDICAL_DOCUMENT if available
      const medicalDocumentService = subscriptionStatus?.service_availability?.MEDICAL_DOCUMENT;
      const remainingCredits = medicalDocumentService
        ? medicalDocumentService.available
        : (subscriptionStatus?.total_available ?? (subscriptionStatus?.creds + subscriptionStatus?.extra_credits - subscriptionStatus?.counts));
      const canUse = medicalDocumentService
        ? medicalDocumentService.can_use
        : remainingCredits > 0;

      if (subscriptionStatus && ProcessType === 'AI') {
        if (!canUse) {
          // Check active subscription using details (new field) or subscriptions (deprecated) or currentSubs (legacy)
          const subscriptionDetails = subscriptionStatus?.subscription_credits?.details;
          const subscriptionSubs = subscriptionStatus?.subscription_credits?.subscriptions;
          const legacySubs = subscriptionStatus?.currentSubs;
          const isActiveSubsription = (subscriptionDetails && subscriptionDetails.length > 0) 
            || (subscriptionSubs && subscriptionSubs.length > 0) 
            || (legacySubs && legacySubs.length > 0);
          const title = isActiveSubsription
            ? t('document-form.actve-subscription-title1')
            : t('document-form.actve-subscription-title2');
          const text = isActiveSubsription
            ? t('document-form.actve-subscription-text1')
            : t('document-form.actve-subscription-text2');

          alert
            .fire({
              icon: 'warning',
              title,
              text,
              showCancelButton: true,
              confirmButtonText: t('document-form.no-ai-process-button-confirm'),
              cancelButtonText: t('document-form.no-ai-process-button-cancel'),
            })
            .then((result) => {
              setValue('process_type', result.isConfirmed ? 'MANUAL' : 'POSTPONE');
            });
        }
      }
    }
  }, [ProcessType, navigate, setValue, subscriptionStatus, type, t]);

  const getUpdatedValues = ({ fields = dirtyFields, values }) => {
    return Object.keys(fields).reduce((acc, field) => {
      if (fields[field]) acc[field] = values[field];
      return acc;
    }, {});
  };

  const onSubmit = async (params) => {
    let medicalDocument;

    if (params.json_content) {
      if (params.document_type === 'LAB') {
        params.json_content = params.json_content.reduce((acc, data) => {
          if (data.icd) {
            data.values = data.values.filter((value) => value.name);
            acc.push(data);
          }
          return acc;
        }, []);

        params.json_content = params.json_content.map((lab) => {
          if (lab.icd.value) {
            const namePl = lab.icd.namePl || lab.namePl;
            const nameEn = lab.icd.nameEn || lab.nameEn;
            const currentLanguage = lab.icd.currentLanguage || lab.currentLanguage || i18n.language;
            return {
              ...lab,
              category_name: currentLanguage === 'pl' ? namePl : nameEn,
              icd: lab.icd.value,
              namePl,
              nameEn,
              currentLanguage,
            };
          }
          return lab;
        });
      } else if (params.document_type === 'DIAG') {
        params.json_content = params.json_content.filter(
          (category) => category.category_name && category.values && category.values.length > 0,
        );
      }
    }

    if (type === 'EDIT') {
      const { data: updatedDocument } = await updateMedicalDocumentMutation({
        id: params.id,
        ...getUpdatedValues({ values: params }),
      });
      medicalDocument = updatedDocument;
      if (updatedDocument) {
        alert.fire({
          icon: 'success',
          title: t('document-form.document-updated-title'),
          text: t('document-form.document-updated-text'),
          showButtonText: false,
          timer: 3000,
        });
      }
    } else {
      const { data: createdDocument } = await createMedicalDocumentMutation(params);
      medicalDocument = createdDocument;

      if (medicalDocument?.id) {
        if (medicalDocument.process_type === 'AI') {
          const documentImage = await processMedicalDocumentMutation({
            uuid: medicalDocument.uuid,
          });
          console.log('[AI] Processing document started: ', documentImage);
        }

        alert.fire({
          icon: 'success',
          title: t('document-form.document-created-title'),
          text: `${t('document-form.document-created-text1')} ${
            medicalDocument.process_type === 'AI' ? t('document-form.document-created-text2') : ''
          }`,
          showButtonText: false,
          timer: 3000,
        });
      }
    }

    if (medicalDocument?.id) {
      if (medicalDocument.process_type === 'MANUAL') {
        navigate(generatePath(Routes.MedicalDocument.path, { id: medicalDocument.id }));
      } else {
        navigate(generatePath(Routes.MedicalDocumentsList.path));
      }
    } else {
      // TODO: something went wrong
    }
  };

  const Images = () => (
    <Card className='mb-4'>
      <Card.Header className='text-center mb-3'>
        <h4>{t('document-form.document-images-header')}</h4>
      </Card.Header>
      <Card.Body>
        <ImageGallery
          showFullscreenButton={false}
          showPlayButton={false}
          showNav={false}
          items={data.images?.map((image) => {
            return {
              original: image.signed_url,
              thumbnail: image.thumbnail_signed_url,
            };
          })}
        />
      </Card.Body>
    </Card>
  );

  const DocumentInProgress = (
    <Card>
      <Card.Body className='d-flex flex-column align-items-center m-5'>
        <h4 className='m-4'>{t('document-form.processing-in-progress')}</h4>
        <Spinner
          className='m-4'
          style={{ width: '5rem', height: '5rem', fontSize: '2rem' }}
        ></Spinner>
      </Card.Body>
    </Card>
  );
  const DocumentTypes = [
    {
      name: t('document-types.diag'),
      value: 'DIAG',
    },
    {
      name: t('document-types.lab'),
      value: 'LAB',
    },
  ];

  const DocumentTypeForm = (
    <Card className='mb-4'>
      <Card.Header className='text-center mb-3'>
        <h4>{t('document-form.choose-type')}</h4>
      </Card.Header>
      <Card.Body>
        <SelectBox name='document_type' options={DocumentTypes} type='radio' form={form} />
      </Card.Body>
    </Card>
  );

  const DocumentImagesForm = (
    <Card className='mb-4'>
      <Card.Header className='text-center'>
        <h4>
          {displayDocumentType === DocumentTypes[0].value
            ? t('document-form.upload-image-diag')
            : t('document-form.upload-image-lab')}
        </h4>
      </Card.Header>
      <Card.Body>
        <ImageInput name='document_images' form={form} />
      </Card.Body>
      <Card.Footer>
        <Alert variant='light'>
          <Alert.Heading>{t('image.title')}</Alert.Heading>
          {t('image.warning')}
        </Alert>
      </Card.Footer>
    </Card>
  );

  const ProcessTypes = [
    {
      name: t('process-types.manual'),
      value: 'MANUAL',
    },
    {
      name: t('process-types.ai'),
      value: 'AI',
    },
    {
      name: t('process-types.postpone'),
      value: 'POSTPONE',
    },
  ];

  const ProcessingTypeForm = (
    <Card className='mb-4'>
      <Card.Body>
        <Card.Header className='text-center mb-3'>
          <h4>{t('document-form.choose-processing-type')}</h4>
        </Card.Header>
        <SelectBox name='process_type' options={ProcessTypes} type='radio' form={form} />
      </Card.Body>
    </Card>
  );

  const LabCreateForm = (
    <Row>
      <Col xs={6}>
        <LaboratoryForm form={form} data={data} />
      </Col>
      <Col xs={6}>
        <DocumentInfoForm form={form} data={data} />
      </Col>
    </Row>
  );

  const LabEditForm = (
    <Row>
      <Col xs={12}>
        <DocumentInfoForm form={form} data={data} />
      </Col>
      <Col xs={6} style={scrollableColumnStyle}>
        {ProcessType === 'AI' && data?.process_status === 'IN_PROGRESS' ? (
          DocumentInProgress
        ) : (
          <LaboratoryForm form={form} data={data} />
        )}
      </Col>
      <Col xs={6} style={stickyColumnStyle}>
        <Images />
      </Col>
    </Row>
  );

  const DiagCreateForm = (
    <Row>
      <Col xs={6}>
        <DiagnosisForm form={form} />
      </Col>
      <Col xs={6}>
        <DocumentInfoForm form={form} data={data} />
      </Col>
    </Row>
  );

  const DiagEditForm = (
    <Row>
      <Col xs={12}>
        <DocumentInfoForm form={form} data={data} />
      </Col>
      <Col xs={6} style={scrollableColumnStyle}>
        {ProcessType === 'AI' && data?.process_status === 'IN_PROGRESS' ? (
          DocumentInProgress
        ) : (
          <DiagnosisForm form={form} />
        )}
      </Col>
      <Col xs={6} style={stickyColumnStyle}>
        <Images />
      </Col>
    </Row>
  );

  const InfoCard = () => (
    <Card className='mb-4'>
      <Card.Body>
        <Alert variant='logo-light-green'>
          <Alert.Heading>{t('document-form.info-card-title')}</Alert.Heading>
          <p>{t('document-form.info-card-text')}</p>
        </Alert>
      </Card.Body>
    </Card>
  );

  return type === 'EDIT' && !data ? (
    <div className='d-flex justify-content-center py-8'>
      <Spinner animation='grow' style={{ width: '100px', height: '100px' }} />
    </div>
  ) : (
    <div style={{ position: 'relative' }}>
      {type === 'EDIT' && <InfoCard />}
      <Form onSubmit={handleSubmit(onSubmit)} encType='multipart/form-data'>
        {type === 'CREATE' ? (
          <>
            {DocumentTypeForm}
            {displayDocumentType ? <div className='mt-3'>{DocumentImagesForm}</div> : null}
            {DocumentImages?.length > 0 ? <div className='mt-3'>{ProcessingTypeForm}</div> : null}
            {ProcessType ? (
              <div className='mt-3'>
                {ProcessType === 'MANUAL' ? (
                  <>{displayDocumentType === 'DIAG' ? DiagCreateForm : LabCreateForm}</>
                ) : (
                  <Card className='mb-4'>
                    <Card.Body>
                      <TagsInput form={form} initialValues={data?.tags} title={t('lab.tags')} />
                    </Card.Body>
                  </Card>
                )}
                <Row>
                  <Col xs={12} className='d-grid'>
                    <Button variant='logo-green' size='lg' type='submit' className='animate-up-2'>
                      {isLoadingUpdate || isLoadingCreate ? (
                        <Spinner />
                      ) : (
                        t('document-form.document-submit-button')
                      )}
                    </Button>
                  </Col>
                </Row>
              </div>
            ) : null}
          </>
        ) : (
          <>
            {DocumentType === 'DIAG' ? DiagEditForm : LabEditForm}
            <Row>
              <Col sm={6} className='d-grid'>
                <Button
                  variant='warning'
                  size='lg'
                  onClick={() => {
                    navigate(generatePath(Routes.MedicalDocument.path, { id: data.id }));
                  }}
                  className='animate-up-2'
                >
                  {t('document-form.document-discard-changes-button')}
                </Button>
              </Col>
              <Col sm={6} className='d-grid'>
                <Button variant='logo-green' size='lg' type='submit' className='animate-up-2'>
                  {isLoadingUpdate || isLoadingCreate ? (
                    <Spinner />
                  ) : (
                    t('document-form.document-submit-button')
                  )}
                </Button>
              </Col>
            </Row>
          </>
        )}
      </Form>
    </div>
  );
};
